import React from 'react';
import { Alert } from '@planne-software/uni/Alert';
import { Button } from '@planne-software/uni/Button';
import { IconAlertTriangle } from '@tabler/icons-react';

import { useTranslate } from '@src/core/hooks';
import { resolveTransactableEntity, type Transactable } from '@src/domains/transactables';
import { useRestoreCartFromOrder } from '@src/features/cart';

type ExpiredTransactableAlertProps = {
  transactable: Transactable;
};

export const ExpiredTransactableAlert = (props: ExpiredTransactableAlertProps) => {
  const { transactable } = props;
  const { i18n } = useTranslate();

  const { restore, restoring } = useRestoreCartFromOrder();

  // Every transactable kind reaches this step, but only an order can be turned back into a cart —
  // and only when the order is what the buyer was paying: a sale change rides on top of one without
  // being it, and a sale or a direct link never had a cart. The others keep the notice and lose the
  // action, rather than getting a button that rebuilds the wrong purchase.
  const order = resolveTransactableEntity(transactable) === transactable.order ? transactable.order : undefined;

  return (
    <Alert
      data-testid='expired-transactable-alert'
      severity='warning'
      startIcon={<IconAlertTriangle size={20} aria-hidden />}
      title={i18n('checkout_payment_error_expired_title')}
      subTitle={i18n('checkout_payment_error_expired_description')}
      action={
        order?.id ? (
          <Button
            size='xs'
            variant='slim'
            color='neutral'
            disabled={restoring}
            loading={restoring}
            onClick={() => restore(order.id)}
          >
            {i18n('checkout_payment_error_expired_retry')}
          </Button>
        ) : null
      }
    />
  );
};

export default ExpiredTransactableAlert;
